"use client";
import { DateTime } from "luxon";
import Link from "next/link";
import { EventItem } from "~/app/beta/events/_components/EventItem";
import Spinner from "~/components/themed/spinner";
import { If } from "~/components/v3/Conditional";
import { Button } from "~/components/v4/themed/Button";
import { cn } from "~/utils/fontsConfig";
import { getFormattedTimeStamp, groupAndSortByDate } from "~/utils/time";
import { GetHomeEvents } from "../actions";
import CalendarIcon0Events from "../../[communitySlug]/_components/icons/CalendarIcon0Events";

import {
    GetCommunitiesToHost,
    GetCurrentUserProfile,
} from "../../events/create/actions";
import { CreateEventButton } from "./CreateEventModal";
import { FaPlus } from "react-icons/fa";

const bgVariants = {
    black: "from-black to-black/0",
    card: "from-v3-card-gray to-v3-card-gray/0",
};

type HomeEventsListProps = {
    user: GetCurrentUserProfile;
    events: (
        | GetHomeEvents["hostingEvents"][number]
        | GetHomeEvents["attendingEvents"][number]
    )[];
    hasMore: boolean;
    onShowMore: () => void;
    isLoading: boolean;
    isPast: boolean;
    communities: GetCommunitiesToHost;
};

export const HomeEventsList = ({
    user,
    events,
    hasMore,
    onShowMore,
    isLoading,
    isPast,
    communities,
}: HomeEventsListProps) => {
    const getTimeFilterName = (isPast: boolean) => {
        if (isPast) return "past";
        return "upcoming";
    };
    const timeFilterName = getTimeFilterName(isPast);
    return (
        <div className="flex w-full flex-col">
            <If condition={isLoading}>
                <Spinner />
            </If>
            <If condition={!isLoading}>
                <>
                    <If condition={events?.length === 0}>
                        <div className="flex w-full flex-col items-center justify-center gap-2">
                            <span>No {timeFilterName} events found.</span>

                            <span>Let's change that!</span>
                            <CalendarIcon0Events />
                            <CreateEventButton
                                variant="badge"
                                user={user}
                                communities={communities}
                                size="sm"
                                Icon={<FaPlus />}
                            />
                        </div>
                    </If>
                    <If condition={events?.length > 0}>
                        {groupAndSortByDate(
                            events,
                            isPast ? "desc" : "asc",
                        ).map(([date, eventsGroup], index) => {
                            const groupDate = DateTime.fromMillis(date);
                            const eventGroupIsToday =
                                DateTime.fromMillis(date).diffNow("days")
                                    .days <= 0;
                            return (
                                <div key={date}>
                                    <div
                                        className={cn(
                                            bgVariants["card"],
                                            "sticky top-20 z-[110] w-full",
                                            "bg-gradient-to-b py-2",
                                            "flex items-center gap-2",
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                "absolute left-[10px] w-[1px]",
                                                "bg-v3-N-450",
                                                index === 0
                                                    ? "top-1/2 h-1/2"
                                                    : "top-0 h-full",
                                            )}
                                        />
                                        <div
                                            className={cn(
                                                "z-[1] ml-[5px] h-[10px] w-[10px] rounded-full border-[1px] border-v3-N-400",
                                                eventGroupIsToday
                                                    ? "bg-white"
                                                    : "bg-v3-N-500",
                                            )}
                                        />
                                        <div className="w-max rounded-full bg-v3-N-500 px-3 py-2 text-[14px]">
                                            {getFormattedTimeStamp(
                                                groupDate.toJSDate(), // Use the DateTime object directly
                                                { showYear: false },
                                            )}
                                        </div>
                                    </div>
                                    {eventsGroup.map((event) => (
                                        <EventItem
                                            key={event.id}
                                            event={event}
                                            userId={user.id}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </If>
                    {hasMore && (
                        <Button
                            className="mt-4"
                            onClick={onShowMore}
                            variant="tertiary"
                            disabled={isLoading}
                        >
                            {isLoading ? "Loading..." : "Show More"}
                        </Button>
                    )}
                </>
            </If>
        </div>
    );
};
